import React, { useState, useEffect, useRef, useMemo } from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { TrackVIntegration } from '../TrackVIntegration';
import { RsvpForm } from '@/src/modules/rsvp';

const META: TemplateMetadata = {
  id: 'stitch-immersive-wedding',
  name: 'Stitch Immersive Wedding',
  occasionType: 'WEDDING',
  description:
    'A macOS-inspired glassmorphism wedding invitation with immersive parallax background, animated dock navigation, cycling name greeter, and floating glass windows.',
  isPremium: true,
  tags: ['glassmorphism', 'macos', 'immersive', 'animated', 'wedding', 'premium'],
  colorScheme: { primary: '#000000', accent: '#2b82f4', bg: '#faf8fe' },
};

function parseDateParts(iso: string | undefined) {
  if (!iso) return { day: '24', weekday: 'Sat', month: 'Nov', year: '2024' };
  try {
    const d = new Date(iso);
    return {
      day: String(d.getDate()),
      weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
      month: d.toLocaleDateString('en-US', { month: 'short' }),
      year: String(d.getFullYear()),
    };
  } catch {
    return { day: '24', weekday: 'Sat', month: 'Nov', year: '2024' };
  }
}

export function StitchImmersiveWedding({ data }: { data: InvitationData }) {
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [greetingIdx, setGreetingIdx] = useState(0);
  const [showDestination, setShowDestination] = useState(false);
  const [destinationAnimating, setDestinationAnimating] = useState(false);
  const wallpaperRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);

  const groomName =
    data.customData?.wedding?.groom?.fullName ||
    data.customData?.wedding?.groom?.nickname ||
    'Aarav';
  const brideName =
    data.customData?.wedding?.bride?.fullName ||
    data.customData?.wedding?.bride?.nickname ||
    'Ishani';

  const coupleName = `${groomName} & ${brideName}`;
  const { day, weekday } = parseDateParts(data.eventDate);

  const greetings = [
    { text: coupleName, style: { fontFamily: 'Inter, sans-serif' } },
    { text: `${groomName} and ${brideName}`, style: { fontFamily: "'Playfair Display', serif" } },
    { text: `${groomName[0]} & ${brideName[0]}`, style: { fontFamily: "'Great Vibes', cursive", fontSize: '96px' } },
    { text: coupleName, style: { fontFamily: "'Cinzel', serif" } },
  ];

  const events = useMemo(() => {
    const customEvents = data.customData?.wedding?.weddingEvents;
    if (customEvents && customEvents.length > 0) {
      return customEvents.map((e, i) => ({
        title: e.title,
        date: '',
        time: e.startTime || '',
        venue: e.venue || data.eventVenue || '',
        note: e.description || '',
        icon: i === 0 ? 'music_note' : 'wb_sunny',
        gradient: i === 0 ? 'linear-gradient(135deg, #f87171, #ec4899)' : 'linear-gradient(135deg, #fef08a, #fbbf24)',
      }));
    }
    return [
      {
        title: 'Sangeet',
        date: 'Friday, Nov 23rd',
        time: '7:00 PM Onwards',
        venue: 'The Grand Ballroom',
        note: 'Join us for an evening of music, dance, and celebration. Bring your dancing shoes!',
        icon: 'music_note',
        gradient: 'linear-gradient(135deg, #f87171, #ec4899)',
      },
      {
        title: 'Haldi',
        date: `Saturday, ${data.eventDate ? new Date(data.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Nov 24th'}`,
        time: '10:00 AM',
        venue: data.eventVenue || 'The Courtyard Gardens',
        note: 'Dress code: Shades of Yellow. Get ready to be covered in turmeric and love.',
        icon: 'wb_sunny',
        gradient: 'linear-gradient(135deg, #fef9c3, #fbbf24)',
      },
    ];
  }, [data]);

  const bgImage =
    data.mediaUrls?.[0] ||
    'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=2000&q=80';

  const enableDepthParallax = data.customData?.wedding?.enableDepthParallax === true;

  // Inject fonts + keyframe CSS
  useEffect(() => {
    const fontLinks = [
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap',
      'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;1,600&family=Great+Vibes&family=Cinzel:wght@600&display=swap',
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap',
    ];
    const added: HTMLLinkElement[] = [];
    for (const href of fontLinks) {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const el = document.createElement('link');
        el.rel = 'stylesheet';
        el.href = href;
        document.head.appendChild(el);
        added.push(el);
      }
    }

    const styleId = 'stitch-immersive-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .stitch-mso { font-family: 'Material Symbols Outlined'; font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
        .stitch-mso-fill { font-family: 'Material Symbols Outlined'; font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }

        @keyframes stitchFadeInOut {
          0%, 100% { opacity: 0; transform: scale(0.95); filter: blur(4px); }
          10%, 90% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        .stitch-fade { animation: stitchFadeInOut 4s ease-in-out infinite; }

        @keyframes stitchPulse {
          0% { transform: scale(1); } 50% { transform: scale(1.15); } 100% { transform: scale(1); }
        }
        .stitch-pulse { animation: stitchPulse 2s infinite ease-in-out; }

        @keyframes stitchFloat {
          0% { transform: translateY(0); } 50% { transform: translateY(-5px); } 100% { transform: translateY(0); }
        }
        .stitch-float { animation: stitchFloat 3s ease-in-out infinite; }

        @keyframes stitchSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .stitch-enter { animation: stitchSlideUp 0.8s cubic-bezier(0.4,0,0.2,1) forwards; opacity: 0; }
        .stitch-delay-1 { animation-delay: 100ms; }
        .stitch-delay-2 { animation-delay: 200ms; }
        .stitch-delay-3 { animation-delay: 300ms; }
        .stitch-delay-4 { animation-delay: 400ms; }

        @keyframes stitchWindowIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes stitchWindowOut {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to   { opacity: 0; transform: scale(0.95) translateY(10px); }
        }
        .stitch-win-enter { animation: stitchWindowIn 0.4s cubic-bezier(0.4,0,0.2,1) forwards; }
        .stitch-win-exit  { animation: stitchWindowOut 0.3s cubic-bezier(0.4,0,0.2,1) forwards; }

        @keyframes stitchDockBounce {
          0%   { transform: translateY(100%) translateX(-50%); opacity: 0; }
          60%  { transform: translateY(-10%) translateX(-50%); opacity: 1; }
          80%  { transform: translateY(5%) translateX(-50%); }
          100% { transform: translateY(0) translateX(-50%); }
        }
        .stitch-dock-bounce { animation: stitchDockBounce 1s cubic-bezier(0.4,0,0.2,1) forwards; }

        .stitch-glass {
          background: rgba(255,255,255,0.6);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255,255,255,0.5);
          box-shadow: 0 8px 32px rgba(0,0,0,0.1);
        }
        .stitch-glass-hover:hover {
          transform: translateY(-4px) scale(1.005);
          box-shadow: 0 40px 80px rgba(0,0,0,0.15);
          transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s cubic-bezier(0.4,0,0.2,1);
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      added.forEach(el => el.parentNode?.removeChild(el));
    };
  }, []);

  // Parallax on scroll + mouse
  useEffect(() => {
    const wallpaper = wallpaperRef.current;
    if (!wallpaper) return;
    const onScroll = () => {
      wallpaper.style.transform = `scale(1.05) translateY(${window.scrollY * 0.1}px)`;
    };
    const onMouse = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 10;
      const y = (e.clientY / window.innerHeight - 0.5) * 10;
      wallpaper.style.transform = `scale(1.05) translate(${x}px, ${y}px) translateY(${window.scrollY * 0.1}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  // Greeting cycler
  useEffect(() => {
    const t = setInterval(() => setGreetingIdx(i => (i + 1) % greetings.length), 4000);
    return () => clearInterval(t);
  }, [greetings.length]);

  // Dock magnification
  const onDockMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const dock = dockRef.current;
    if (!dock) return;
    dock.querySelectorAll<HTMLElement>('.stitch-dock-icon').forEach(icon => {
      const rect = icon.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const dist = Math.abs(e.clientX - center);
      const scale = Math.max(1, 1.4 - dist / 150);
      icon.style.transform = `scale(${scale}) translateY(-${(scale - 1) * 15}px)`;
    });
  };
  const onDockMouseLeave = () => {
    dockRef.current?.querySelectorAll<HTMLElement>('.stitch-dock-icon').forEach(icon => {
      icon.style.transform = '';
    });
  };

  const toggleDestination = () => {
    if (!showDestination) {
      setShowDestination(true);
      setDestinationAnimating(true);
    } else {
      setDestinationAnimating(false);
      setTimeout(() => setShowDestination(false), 300);
    }
  };

  const macControls = (
    <div className="absolute top-4 right-6 flex gap-2 z-20">
      <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(0,0,0,0.2)' }} />
      <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(126,117,118,0.5)' }} />
      <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(186,26,26,0.8)' }} />
    </div>
  );

  const macControlsLeft = (onClose?: () => void) => (
    <div className="flex gap-2 absolute left-4">
      <button
        aria-label="Close window"
        className="w-3 h-3 rounded-full"
        style={{ background: 'rgba(186,26,26,0.8)' }}
        onClick={onClose}
      />
      <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(126,117,118,0.5)' }} />
      <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(0,0,0,0.2)' }} />
    </div>
  );

  const heroContent = (
    <>
      <div
        className="rounded-3xl p-8 md:p-20 flex flex-col items-center justify-center max-w-4xl w-full text-center relative overflow-hidden stitch-glass stitch-glass-hover stitch-enter stitch-delay-1"
      >
        {macControls}

        {/* Date widget */}
        <div
          className="absolute top-4 left-6 w-16 h-16 rounded-2xl flex flex-col items-center justify-center overflow-hidden z-20 stitch-float border stitch-enter stitch-delay-3"
          style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(12px)', borderColor: 'rgba(255,255,255,0.8)' }}
        >
          <div className="w-full text-center py-0.5 text-[10px] font-bold text-white uppercase tracking-wider" style={{ background: '#ba1a1a' }}>
            {weekday}
          </div>
          <div className="text-2xl font-bold leading-none mt-1">{day}</div>
        </div>

        {/* Cycling greeting */}
        <div className="h-[120px] flex items-center justify-center overflow-hidden w-full mt-3 relative z-10">
          <h1
            key={greetingIdx}
            className="text-4xl md:text-[80px] leading-[1.1] font-bold tracking-tight stitch-fade drop-shadow-sm"
            style={{ ...greetings[greetingIdx].style, color: '#000' }}
          >
            {greetings[greetingIdx].text}
          </h1>
        </div>

        <p
          className="text-base md:text-lg mt-3 max-w-2xl mx-auto font-light tracking-wide relative z-10 stitch-enter stitch-delay-3"
          style={{ color: '#4c4546' }}
        >
          {data.customMessage ||
            'We invite you to join us in celebrating our journey of love, surrounded by family, friends, and the serene beauty of our destination.'}
        </p>

        <div className="mt-12 flex flex-col sm:flex-row gap-4 relative z-10 stitch-enter stitch-delay-4">
          <RsvpForm
            invitationId={invitationId}
            isLive={isLive}
            externalLink={data.rsvpLink || null}
          />
          <button
            onClick={() => document.querySelector('#stitch-events')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-3 rounded-full text-xs font-semibold active:scale-95 transition-all shadow-sm"
            style={{
              backdropFilter: 'blur(12px)',
              background: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(207,196,197,0.5)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            VIEW ITINERARY
          </button>
        </div>
      </div>

      {/* Desktop side icons */}
      <div className="hidden lg:flex absolute right-12 flex-col items-center gap-8 z-20 stitch-enter stitch-delay-3" style={{ top: '33%' }}>
        <button aria-label="Toggle Destination" className="group flex flex-col items-center gap-2" onClick={toggleDestination}>
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center border transition-all group-hover:scale-110"
            style={{ backdropFilter: 'blur(20px)', background: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.6)' }}
          >
            <span className="stitch-mso" style={{ fontSize: '36px' }}>map</span>
          </div>
          <span
            className="text-xs font-semibold rounded-full px-3 py-1 border group-hover:scale-105 transition-transform"
            style={{ background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(12px)', borderColor: 'rgba(255,255,255,0.4)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
          >
            Destination
          </span>
        </button>
        <button aria-label="Open Registry" className="group flex flex-col items-center gap-2">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center border transition-all group-hover:scale-110"
            style={{ backdropFilter: 'blur(20px)', background: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.6)' }}
          >
            <span className="stitch-mso" style={{ fontSize: '36px' }}>card_giftcard</span>
          </div>
          <span
            className="text-xs font-semibold rounded-full px-3 py-1 border group-hover:scale-105 transition-transform"
            style={{ background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(12px)', borderColor: 'rgba(255,255,255,0.4)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
          >
            Registry
          </span>
        </button>
      </div>
    </>
  );

  return (
    <div
      className="relative overflow-x-hidden min-h-screen"
      style={{ fontFamily: 'Inter, sans-serif', color: '#1a1b1f', background: '#faf8fe', scrollBehavior: 'smooth' }}
    >
      {/* Wallpaper */}
      <div
        ref={wallpaperRef}
        className="fixed inset-0 bg-cover bg-center"
        style={{ zIndex: -2, backgroundImage: `url('${bgImage}')`, transform: 'scale(1.05)' }}
      />
      {/* Blur overlay */}
      <div className="fixed inset-0" style={{ zIndex: -1, background: 'rgba(250,248,254,0.1)', backdropFilter: 'blur(2px)' }} />

      {/* Header */}
      <header
        className="fixed top-0 w-full z-50 stitch-enter"
        style={{ backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.1)' }}
      >
        <div className="flex justify-between items-center px-6 py-3 max-w-[1200px] mx-auto w-full">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center border"
              style={{ backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              <span className="text-sm font-bold">IC</span>
            </div>
            <span className="text-xl font-bold tracking-tight">InviteCloud</span>
          </div>
          <button
            aria-label="User Account"
            className="w-10 h-10 rounded-full flex items-center justify-center border"
            style={{ backdropFilter: 'blur(4px)', background: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.3)' }}
          >
            <span className="stitch-mso" style={{ fontSize: '20px' }}>account_circle</span>
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="relative flex flex-col min-h-screen pb-40" style={{ zIndex: 10 }}>

        {/* Hero */}
        {enableDepthParallax ? (
          <TrackVIntegration effectType="depth" sectionId="stitch-hero">
            <section className="min-h-[90vh] flex items-center justify-center px-4 md:px-6 relative w-full pt-20">
              {heroContent}
            </section>
          </TrackVIntegration>
        ) : (
          <section id="stitch-hero" className="min-h-[90vh] flex items-center justify-center px-4 md:px-6 relative w-full pt-20">
            {heroContent}
          </section>
        )}

        {/* Events */}
        <section id="stitch-events" className="max-w-[1200px] mx-auto px-4 md:px-6 w-full mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 relative" style={{ zIndex: 20 }}>
          {events.map((event, i) => (
            <div
              key={i}
              className="rounded-3xl p-6 md:p-8 relative overflow-hidden transition-all stitch-glass stitch-glass-hover stitch-enter"
              style={{ animationDelay: `${200 + i * 100}ms` }}
            >
              <div className="absolute top-4 left-6 flex gap-2 z-20">
                <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(186,26,26,0.8)' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(126,117,118,0.5)' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(0,0,0,0.2)' }} />
              </div>
              <div className="mt-6 relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="stitch-mso" style={{ fontSize: '28px' }}>{event.icon}</span>
                  <h2 className="text-2xl font-semibold">{event.title}</h2>
                </div>
                <div className="space-y-3 text-sm" style={{ color: '#4c4546' }}>
                  {event.date && (
                    <p className="flex items-center gap-2">
                      <span className="stitch-mso" style={{ fontSize: '16px' }}>calendar_today</span>
                      {event.date}
                    </p>
                  )}
                  {event.time && (
                    <p className="flex items-center gap-2">
                      <span className="stitch-mso" style={{ fontSize: '16px' }}>schedule</span>
                      {event.time}
                    </p>
                  )}
                  {event.venue && (
                    <p className="flex items-center gap-2">
                      <span className="stitch-mso" style={{ fontSize: '16px' }}>location_on</span>
                      {event.venue}
                    </p>
                  )}
                  {event.note && (
                    <p
                      className="mt-4 text-sm p-3 rounded-xl border"
                      style={{ background: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.5)' }}
                    >
                      {event.note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Photo gallery — shown when 2+ media URLs provided */}
        {data.mediaUrls && data.mediaUrls.length > 1 && (
          <section className="max-w-[1200px] mx-auto px-4 md:px-6 w-full mt-12 relative mb-20" style={{ zIndex: 20 }}>
            <div
              className="rounded-3xl relative overflow-hidden flex flex-col border stitch-glass stitch-glass-hover stitch-enter stitch-delay-4"
              style={{ height: '500px', borderColor: 'rgba(255,255,255,0.6)' }}
            >
              {/* Toolbar */}
              <div
                className="h-14 border-b flex items-center justify-between px-4 z-20 shrink-0"
                style={{ borderColor: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}
              >
                <div className="flex items-center gap-4">
                  <div className="flex gap-2 ml-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(186,26,26,0.8)' }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(126,117,118,0.5)' }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(0,0,0,0.2)' }} />
                  </div>
                  <div className="flex gap-1 ml-4 p-1 rounded-md border" style={{ background: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.4)' }}>
                    <button aria-label="View Photo Library" className="px-3 py-1 rounded bg-white shadow-sm text-xs font-semibold">Library</button>
                    <button aria-label="View Photo Albums" className="px-3 py-1 rounded text-xs font-semibold" style={{ color: '#5d5e60' }}>Albums</button>
                  </div>
                </div>
                <div className="flex items-center gap-2" style={{ color: '#5d5e60' }}>
                  <span className="stitch-mso" style={{ fontSize: '16px' }}>search</span>
                  <span className="stitch-mso" style={{ fontSize: '16px' }}>add</span>
                </div>
              </div>
              {/* Grid */}
              <div className="flex-1 p-4 overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 h-full">
                  <div className="col-span-2 row-span-2 rounded-xl overflow-hidden shadow-sm cursor-pointer">
                    <img
                      src={data.mediaUrls[0]}
                      alt="Gallery"
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                  {data.mediaUrls.slice(1, 7).map((url, idx) => (
                    <div key={idx} className="rounded-lg overflow-hidden shadow-sm cursor-pointer aspect-square">
                      <img
                        src={url}
                        alt="Gallery"
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Floating destination window */}
      {showDestination && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 110, pointerEvents: 'none' }}>
          <div
            className={`rounded-3xl w-full max-w-3xl flex flex-col overflow-hidden shadow-2xl border stitch-glass ${destinationAnimating ? 'stitch-win-enter' : 'stitch-win-exit'}`}
            style={{ height: '360px', borderColor: 'rgba(255,255,255,0.6)', pointerEvents: 'auto' }}
          >
            <div
              className="h-12 border-b flex items-center px-4 shrink-0 relative z-10"
              style={{ borderColor: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(12px)' }}
            >
              {macControlsLeft(toggleDestination)}
              <div className="w-full text-center text-sm font-semibold">Maps</div>
            </div>
            <div className="flex-1 relative flex items-center justify-center" style={{ background: '#e9e5df' }}>
              <div
                className="p-6 rounded-2xl shadow-lg border max-w-sm w-full text-center mx-4"
                style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)' }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white shadow-md"
                  style={{ background: '#ba1a1a' }}
                >
                  <span className="stitch-mso" style={{ fontSize: '20px' }}>pin_drop</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{data.eventVenue || 'The Grand Palace'}</h3>
                <p className="text-sm mb-4" style={{ color: '#5d5e60' }}>{data.eventVenue || 'India'}</p>
                <button
                  className="w-full py-2 rounded-lg font-semibold text-sm text-white hover:opacity-90 transition-opacity"
                  style={{ background: '#2b82f4' }}
                  onClick={() =>
                    window.open(
                      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.eventVenue || '')}`,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  Get Directions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dock */}
      <nav className="fixed bottom-6 w-auto z-50 stitch-dock-bounce" style={{ left: '50%' }}>
        <div
          ref={dockRef}
          className="flex gap-3 items-center justify-center rounded-3xl px-4 py-3 stitch-glass border"
          style={{ borderColor: 'rgba(255,255,255,0.5)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          onMouseMove={onDockMouseMove}
          onMouseLeave={onDockMouseLeave}
        >
          {events.map((event, i) => (
            <button
              key={i}
              aria-label={`View ${event.title}`}
              className="group flex flex-col items-center justify-end p-1 w-16 relative"
              onClick={i === 0 ? toggleDestination : undefined}
            >
              <div
                className="stitch-dock-icon w-12 h-12 rounded-[14px] border shadow-md flex items-center justify-center"
                style={{
                  background: event.gradient,
                  borderColor: i === 0 ? 'rgba(251,182,206,0.5)' : 'rgba(252,211,77,0.5)',
                  transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                  transformOrigin: 'bottom center',
                }}
              >
                <span className="stitch-mso" style={{ fontSize: '24px' }}>{event.icon}</span>
              </div>
              <span
                className="absolute -top-12 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-semibold px-3 py-1 rounded-md shadow-lg pointer-events-none border"
                style={{ background: 'rgba(250,248,254,0.9)', backdropFilter: 'blur(20px)', borderColor: 'rgba(255,255,255,0.2)', letterSpacing: '0.05em' }}
              >
                {event.title}
              </span>
              <div className="w-1 h-1 rounded-full mt-2" style={{ background: 'transparent' }} />
            </button>
          ))}
          {/* Wedding heart */}
          <button aria-label="View Wedding Heart" className="group flex flex-col items-center justify-end p-1 w-16 relative">
            <div
              className="stitch-dock-icon w-12 h-12 rounded-[14px] border shadow-md flex items-center justify-center"
              style={{
                background: 'white',
                borderColor: 'rgba(200,200,200,0.5)',
                transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                transformOrigin: 'bottom center',
              }}
            >
              <span className="stitch-mso-fill stitch-pulse" style={{ fontSize: '28px', color: '#ba1a1a' }}>
                favorite
              </span>
            </div>
            <span
              className="absolute -top-12 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-semibold px-3 py-1 rounded-md shadow-lg pointer-events-none border"
              style={{ background: 'rgba(250,248,254,0.9)', backdropFilter: 'blur(20px)', borderColor: 'rgba(255,255,255,0.2)', letterSpacing: '0.05em' }}
            >
              Wedding
            </span>
            <div className="w-1 h-1 rounded-full mt-2" style={{ background: 'rgba(0,0,0,0.7)' }} />
          </button>
        </div>
      </nav>

      {/* Footer */}
      <footer
        className="w-full mt-20 relative border-t pb-40"
        style={{ zIndex: 10, borderColor: 'rgba(255,255,255,0.2)', background: 'transparent' }}
      >
        {!data.isPremium && (
          <div className="flex flex-col items-center gap-3 py-12 max-w-[1200px] mx-auto px-6">
            <div
              className="text-xs font-semibold rounded-full px-4 py-1 border"
              style={{
                color: 'rgba(0,0,0,0.7)',
                background: 'rgba(255,255,255,0.3)',
                backdropFilter: 'blur(4px)',
                borderColor: 'rgba(255,255,255,0.4)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              INVITECLOUD
            </div>
            <div
              className="flex gap-6 mt-2 rounded-full px-6 py-2 border"
              style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', borderColor: 'rgba(255,255,255,0.3)' }}
            >
              {['Privacy', 'Terms', 'Support'].map(link => (
                <a key={link} href="#" className="text-sm font-medium transition-colors" style={{ color: 'rgba(0,0,0,0.8)' }}>
                  {link}
                </a>
              ))}
            </div>
            <p className="text-sm mt-4" style={{ color: 'rgba(0,0,0,0.6)' }}>
              © {new Date().getFullYear()} InviteCloud. All rights reserved.
            </p>
          </div>
        )}
      </footer>
    </div>
  );
}

registry.register({ metadata: META, component: StitchImmersiveWedding });
